/* eslint-disable import/prefer-default-export */
import BaseFeature from '@appblocks/codegeneration/BaseFeature';
import ProjectConfiguration from '@appblocks/codegeneration/ProjectConfiguration';
import {
    CodeBlock,
    BlockType,
    SettingType,
    Define,
    ValidationMessageType,
    ValidationMessage,
    SettingState,
    Setting,
    TibboDataType,
} from '@appblocks/codegeneration/types';
import BaseNode from '@appblocks/codegeneration/Nodes/BaseNode';
import NodePort from '@appblocks/codegeneration/Nodes/NodePort';
import NodeParameter from '@appblocks/codegeneration/Nodes/NodeParameter';

export const getCodeBlocks = (feature: BaseFeature): CodeBlock[] => {
    const blocks: Array<CodeBlock> = [];
    const { project } = feature;

    blocks.push({
        location: 'GLOBAL_INCLUDES',
        id: '#include <zephyr/net/http/client.h>',
        blockType: BlockType.CODE,
        template: '#include <zephyr/net/http/client.h>',
    });

    blocks.push({
        location: 'GLOBAL_INCLUDES',
        id: '#include <zephyr/net/http/parser_url.h>',
        blockType: BlockType.CODE,
        template: '#include <zephyr/net/http/parser_url.h>',
    });

    blocks.push({
        location: 'PRJ_CONF',
        id: 'CONFIG_HTTP_CLIENT=y',
        blockType: BlockType.CODE,
        template: 'CONFIG_HTTP_CLIENT=y',
    });

    blocks.push({
        location: 'FUNCTION_DECLARATIONS',
        blockType: BlockType.CODE,
        template: `
static void http_dns_request_result(enum dns_resolve_status status, struct dns_addrinfo *info,
                        void *user_data);
`,
    });

    blocks.push({
        location: 'FUNCTION_DEFINITIONS',
        blockType: BlockType.CODE,
        template: `
static void http_dns_request_result(enum dns_resolve_status status, struct dns_addrinfo *info,
                        void *user_data)
{
    LOG_INF("DNS request result: %d", status);
    if (status != DNS_EAI_INPROGRESS) {
        return;
    }
    
    http_dns_addrinfo = *info;
    LOG_INF("DNS request completed");
    k_sem_give(&dns_query_http_sem);
}`,
    });

    blocks.push({
        location: 'GLOBAL_DEFINES',
        blockType: BlockType.CODE,
        template: `
K_SEM_DEFINE(dns_query_http_sem, 0, 1);
`,
    });

    blocks.push({
        location: 'FUNCTION_DECLARATIONS',
        blockType: BlockType.CODE,
        template: `
static int setup_http_socket(const char* url, struct http_parser_url *parser);`,
    });

    blocks.push({
        location: 'VARIABLE_DECLARATIONS',
        blockType: BlockType.CODE,
        template: `
static struct dns_addrinfo http_dns_addrinfo;
`,
    });

    blocks.push({
        location: 'FUNCTION_DEFINITIONS',
        blockType: BlockType.CODE,
        template: `
static int setup_http_socket(const char* url, struct http_parser_url *parser)
{
    char hostname[256] = {0};
    int port = 80; // default HTTP port
    char port_str[6] = {0};
    bool secure = false;
    const char* original_url = url; // Keep original URL for parser offsets
    
    // Check if URL starts with protocol
    if (strncmp(url, "http://", 7) == 0) {
        port = 80;
        secure = false;
    } else if (strncmp(url, "https://", 8) == 0) {
        port = 443;
        secure = true;
    }

    // Use original URL with parser offsets
    if (parser->field_set & (1 << UF_HOST)) {
        strncpy(hostname, original_url + parser->field_data[UF_HOST].off, parser->field_data[UF_HOST].len);
        hostname[parser->field_data[UF_HOST].len] = '\\0';
    }
    
    if (parser->field_set & (1 << UF_PORT)) {
        strncpy(port_str, original_url + parser->field_data[UF_PORT].off, parser->field_data[UF_PORT].len);
        port_str[parser->field_data[UF_PORT].len] = '\\0';
    } else {
        // use default port based on protocol
        strcpy(port_str, secure ? "443" : "80");
    }
    

    struct addrinfo *result;
    const struct addrinfo hints = {
        .ai_family = AF_INET,
        .ai_socktype = SOCK_STREAM
    };

    int rc = getaddrinfo(hostname, port_str, &hints, &result);
    int MAX_RETRIES = 3;
    int retry_count = 0;
    while (rc != 0 && retry_count < MAX_RETRIES) {
        LOG_INF("Failed to resolve url hostname [%s], retrying...", gai_strerror(rc));
        rc = getaddrinfo(hostname, port_str, &hints, &result);
        k_sleep(K_SECONDS(1));
        retry_count++;
    }
    if (rc != 0) {
        LOG_ERR("Failed to resolve url hostname [%s]", gai_strerror(rc));
        return -EIO;
    }

    // static uint16_t dns_id;

    // // dns request
    // int rc = dns_get_addr_info(hostname,
    // DNS_QUERY_TYPE_A,
    // &dns_id,
    // http_dns_request_result,
    // NULL,
    // 19000);

    // if (k_sem_take(&dns_query_http_sem, K_SECONDS(20)) < 0) {
    //     LOG_ERR("Failed to resolve hostname");
    //     return -EAGAIN;
    // }


    int sock = -1;
    struct sockaddr *addr = result->ai_addr;
    socklen_t addr_len = result->ai_addrlen;
    // struct sockaddr *addr = &http_dns_addrinfo.ai_addr;
    // socklen_t addr_len = http_dns_addrinfo.ai_addrlen;

    // print the IP
    char ip_str[NET_IPV4_ADDR_LEN]; // Large enough for IPv4
    const void *addr_ptr;

    if (addr->sa_family == AF_INET) {
        // IPv4 address
        struct sockaddr_in *addr_in = (struct sockaddr_in *)addr;
        addr_ptr = &addr_in->sin_addr;
        inet_ntop(AF_INET, addr_ptr, ip_str, sizeof(ip_str));
        LOG_INF("Resolved to IPv4 address: %s", ip_str);
    } else if (addr->sa_family == AF_INET6) {
        // IPv6 address
        struct sockaddr_in6 *addr_in6 = (struct sockaddr_in6 *)addr;
        addr_ptr = &addr_in6->sin6_addr;
        inet_ntop(AF_INET6, addr_ptr, ip_str, sizeof(ip_str));
        LOG_INF("Resolved to IPv6 address: %s", ip_str);
    } else {
        LOG_INF("Unknown address family: %d", addr->sa_family);
    }

    if (secure) {
#if defined(CONFIG_NET_SOCKETS_SOCKOPT_TLS)
        sec_tag_t sec_tag_opt[ARRAY_SIZE(ca_certificates)];

        for (int i = 0; i < ARRAY_SIZE(ca_certificates); i++) {
            sec_tag_opt[i] = CA_CERTIFICATE_TAG + i;
        };

        sock = socket(addr->sa_family, SOCK_STREAM, IPPROTO_TLS_1_2);
        if (sock >= 0) {
            int ret = 0;
            ret = setsockopt(sock, SOL_TLS, TLS_SEC_TAG_LIST,
                sec_tag_opt, sizeof(sec_tag_opt));
            if (ret < 0) {
                LOG_ERR("Failed to set %s secure option (%d)",
                    addr->sa_family, -errno);
                return -errno;
            }

            int verify = TLS_PEER_VERIFY_OPTIONAL;
            ret = setsockopt(sock, SOL_TLS, TLS_PEER_VERIFY, &verify, sizeof(verify));

            ret = setsockopt(sock, SOL_TLS, TLS_HOSTNAME,
                        hostname,
                        sizeof(hostname));
            if (ret < 0) {
                LOG_ERR("Failed to set %s TLS_HOSTNAME "
                    "option (%d)", addr->sa_family, -errno);
                return -errno;
            }
        }
#endif            
    } else {
        sock = socket(addr->sa_family, SOCK_STREAM, IPPROTO_TCP);
    }

    
    if (sock < 0) {
        LOG_ERR("Failed to create socket: %d", errno);
        freeaddrinfo(result);
        return -EIO;
    }
    // connect the socket
    rc = connect(sock, addr, addr_len);
    if (rc < 0) {
        LOG_ERR("Failed to connect to %s:%s (%d)", hostname, port_str, errno);
        close(sock);
        freeaddrinfo(result);
        return -EIO;
    }

    freeaddrinfo(result);
    return sock;
}        
        `,
    });

    return blocks;
};
